/**
 * Diagnostics card for the single-entity dashboard's "Bank Balances by
 * month" chart snapshot job (dashboard redesign step 4). Same shape as
 * CashJurisdictionSnapshotPanel.jsx: shows last-snapshot time + doc count,
 * a "Run Snapshot Now" button for the daily job, and a Preview/Apply flow
 * for the transaction-based backfill.
 *
 * This is the only way to trigger the backfill/run-now endpoints -- the
 * operator cannot run a shell/curl/script (CLAUDE.md), so a manual
 * DevTools call is not an option; the panel rides the app's own
 * authenticated API client.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Play, Eye, Check, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { bankAccountSnapshotAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function relativeTime(isoDate) {
  if (!isoDate) return 'Never';
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = diff / 3600000;
  if (hours < 1) return Math.round(hours * 60) + 'm ago';
  if (hours < 24) return Math.round(hours) + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

export function BankAccountSnapshotPanel() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  // Default reaches January 1 of the current calendar year regardless of
  // today's month (dashboards round 3's Jan-Dec chart axis): new Date()
  // .getMonth() is 0-indexed and already equals "months since January".
  const [monthsBack, setMonthsBack] = useState(Math.max(1, new Date().getMonth()));
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cleanupPreviewing, setCleanupPreviewing] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await bankAccountSnapshotAPI.diagnostics();
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await bankAccountSnapshotAPI.runNow();
      toast.success('Snapshot taken for the current month');
      // The snapshot itself always succeeds independently of the trailing-
      // history self-heal below it -- a self-heal failure (FX provider down,
      // a transient Mongo timeout) must still be shown, not buried in a log
      // the operator can't read (CLAUDE.md: operator has no shell access).
      if (data?.backfill_error) {
        toast.warning(`Snapshot saved, but auto-backfill of trailing history failed: ${data.backfill_error}`);
      }
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningNow(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await bankAccountSnapshotAPI.backfillPreview(monthsBack);
      setPreview(data);
      if (data.written.length === 0) {
        toast.success('Preview: nothing to backfill (every month already has an exact snapshot)');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.written.length === 0) return;
    if (!window.confirm(
      `Apply backfill for ${preview.written.length} month/account row(s)? `
      + 'This overwrites any existing reconstructed figures for those months.'
    )) return;
    setApplying(true);
    try {
      const { data } = await bankAccountSnapshotAPI.backfillApply(monthsBack);
      toast.success(`Backfill applied: ${data.written.length} row(s) written`);
      setPreview(null);
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const runCleanupPreview = async () => {
    setCleanupPreviewing(true);
    setCleanupPreview(null);
    try {
      const { data } = await bankAccountSnapshotAPI.cleanupPreview();
      setCleanupPreview(data);
      if (data.found.length === 0) {
        toast.success('Preview: no fabricated history found -- nothing to clean up');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupPreviewing(false);
    }
  };

  const runCleanupApply = async () => {
    if (!cleanupPreview || cleanupPreview.found.length === 0) return;
    if (!window.confirm(
      `Delete ${cleanupPreview.found.length} fabricated month/account row(s)? `
      + 'These were reconstructed before the account\'s actual transaction coverage began, '
      + 'so the true balance cannot be recovered -- this restores the honest gap instead.'
    )) return;
    setCleanupApplying(true);
    try {
      const { data } = await bankAccountSnapshotAPI.cleanupApply();
      toast.success(`Cleanup applied: ${data.deleted_count} row(s) deleted`);
      setCleanupPreview(null);
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupApplying(false);
    }
  };

  const bySource = status?.by_source || {};

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Bank Account Balance Snapshot</CardTitle>
            <CardDescription>
              Single-entity dashboard's monthly per-bank balance history ("Bank Balances by month")
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runNow}
              disabled={runningNow}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-bank-account-snapshot-run-now-btn"
            >
              {runningNow ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run Snapshot Now
            </button>
            <button
              onClick={fetchStatus}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load'}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {status && (
          <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground flex-wrap">
            <span>Last updated: {relativeTime(status.last_updated_at)}</span>
            <span>Months covered: {status.earliest_month || '-'} to {status.latest_month || '-'}</span>
            <Badge variant="outline" className="text-[10px]">{status.total_docs} docs</Badge>
            <Badge variant="outline" className="text-[10px]">{status.account_count} accounts</Badge>
            <Badge className="bg-green-100 text-green-700 text-[10px]">{bySource.snapshot || 0} exact</Badge>
            <Badge className="bg-amber-100 text-amber-700 text-[10px]">{bySource.reconstructed || 0} reconstructed</Badge>
          </div>
        )}

        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Reconstruct past months from transaction history, per bank account. Never overwrites
            an exact snapshot. Re-run this after transaction history changes materially --
            reconstructed months are only as good as the transaction history available when this runs.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-muted-foreground" htmlFor="bas-months-back">Months back</label>
            <Input
              id="bas-months-back"
              type="number"
              min={1}
              max={36}
              value={monthsBack}
              onChange={(e) => setMonthsBack(Math.max(1, Math.min(36, parseInt(e.target.value, 10) || 1)))}
              className="h-8 w-20 text-xs"
            />
            <button
              onClick={runPreview}
              disabled={previewing}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-bank-account-snapshot-preview-btn"
            >
              {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
              Preview
            </button>
            <button
              onClick={runApply}
              disabled={applying || !preview || preview.written.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:bg-background disabled:text-foreground"
              data-testid="diag-bank-account-snapshot-apply-btn"
            >
              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Apply
            </button>
          </div>

          {preview && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-[10px]">PREVIEW (read-only)</Badge>
                <span className="text-xs text-muted-foreground">
                  {preview.written.length} row(s) would change, {preview.skipped_existing_snapshot.length} skipped (exact snapshot present)
                </span>
              </div>
              {preview.written.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Would become</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.written.map((row, i) => (
                      <TableRow key={row.month + '-' + row.account_id + '-' + i}>
                        <TableCell className="text-xs">{row.month}</TableCell>
                        <TableCell className="text-xs">{row.bank_name}</TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {row.previous_balance != null ? `${row.currency} ${row.previous_balance.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {row.currency} {row.new_balance.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Find and remove fabricated history: months reconstructed before an account's actual
            transaction coverage began (e.g. by an earlier run, before the coverage guard shipped).
            There is no way to recover the true balance for a fabricated month, so this deletes it
            rather than correcting it -- restoring the honest gap the chart promises for missing
            history, instead of leaving a wrong number in place. Safe to re-run: an empty result
            means nothing needs cleaning up.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={runCleanupPreview}
              disabled={cleanupPreviewing}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-bank-account-snapshot-cleanup-preview-btn"
            >
              {cleanupPreviewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
              Preview cleanup
            </button>
            <button
              onClick={runCleanupApply}
              disabled={cleanupApplying || !cleanupPreview || cleanupPreview.found.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:bg-background disabled:text-foreground disabled:border-input"
              data-testid="diag-bank-account-snapshot-cleanup-apply-btn"
            >
              {cleanupApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete fabricated history
            </button>
          </div>

          {cleanupPreview && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-[10px]">PREVIEW (read-only)</Badge>
                <span className="text-xs text-muted-foreground">
                  {cleanupPreview.found.length} fabricated row(s) found
                </span>
              </div>
              {cleanupPreview.found.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead className="text-right">Fabricated balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cleanupPreview.found.map((row, i) => (
                      <TableRow key={row.month + '-' + row.account_id + '-' + i}>
                        <TableCell className="text-xs">{row.month}</TableCell>
                        <TableCell className="text-xs">{row.bank_name}</TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {row.currency} {row.balance != null ? row.balance.toLocaleString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
